import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import { DataSource } from '@angular/cdk/collections';
import { LinkDocumentAppendixDialogComponent } from '../link-document-appendix-dialog/link-document-appendix-dialog.component';
import { MultiDownloadService } from 'src/app/core/services/multi-download.service';
import { DocumentMohaService } from 'src/app/core/services/document-moha.service';
import { EnumDownloadMenuName } from 'src/app/core/constants/common';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { Message } from 'src/app/core/class/message';
import { CommonService } from 'src/app/core/services/common.service';
import { WarningComponent } from 'src/app/shared/components/warning/warning.component';
import {
  ContractCollectionDocumentResponse,
  ContractCollectionFilterRequest,
  ContractCollectionResponse,
  ViewDossierListService
} from './view-dossier-list.service';

export type DossierPlatformStatus = 'completed' | 'processing' | 'failed' | 'not-sent';
export type DossierCompletionStatus = 'completed-all' | 'incomplete-all';

export interface DossierDocumentView {
  id: string;
  mohaId: string;
  contractCode: string;
  contractName: string;
  customerCode: string;
  employeeName: string;
  certificateNo: string;
  status: DossierPlatformStatus;
  appendix: boolean;
  isCheckStandardized?: boolean;
}

export interface DossierView {
  id: string;
  mohaId: string;
  nameAndCode: string;
  customerCode: string;
  employeeName: string;
  certificateNo: string;
  status: DossierCompletionStatus;
  documents: DossierDocumentView[];
  employeeEnterpriseId?: string;
  raw?: any;
}

export interface DossierListAction {
  type: 'push' | 'push-batch' | 'download' | 'link-appendix' | 'view' | 'delete';
  dossiers?: DossierView[];
  dossier?: DossierView;
  document?: DossierDocumentView;
}

export interface DossierTableRow {
  type: 'dossier' | 'document';
  dossier: DossierView;
  document?: DossierDocumentView;
  number?: number;
}

@Component({
  selector: 'app-view-dossier-list',
  templateUrl: './view-dossier-list.component.html',
  styleUrls: ['./view-dossier-list.component.scss']
})
export class ViewDossierListComponent implements OnInit, OnDestroy {
  @Output() action = new EventEmitter<DossierListAction>();

  filters = {
    contractId: '',
    employee: '',
    dossierStatus: null as (DossierCompletionStatus | null),
    documentStatus: null as (DossierPlatformStatus | null),
    nameOrCode: ''
  };
  filteredDossiers: DossierView[] = [];
  dossiers: DossierView[] = [];
  tableRows: DossierTableRow[] = [];
  dataSource = new DossierTableDataSource();
  displayedColumns = [
    // 'check',
    'expand',
    'STT',
    'mohaUuid',
    'nameAndCode',
    'customerCode',
    'employeeName',
    'certificateNo',
    'status',
    'action'
  ];
  selectedDossiers = new Map<string, DossierView>();
  expandedIds = new Set<string>();
  paginate: DossierPaginate = {
    currentPage: 0,
    pageSize: 10,
    totalRow: 0,
    totalPage: 0,
    totalPageArray: [],
    no: []
  };
  loading = false;
  loadError = '';
  private readonly destroyed = new Subject<void>();

  constructor(
    private dossierService: ViewDossierListService,
    private router: Router,
    private dialog: MatDialog,
    private downloadService: MultiDownloadService,
    private documentMohaService: DocumentMohaService,
    private toastr: ToastrService,
    public message: Message,
    public commonService: CommonService
  ) { }

  downloadContracts(contract: any, appendix?: any) {
    let target = appendix || contract;
    if (!target || !target.id) {
      return;
    }
    const targetId = target.id;
    const targetName = target.contractName || target.name || target.nameAndCode || (contract ? contract.nameAndCode || contract.name : '') || '';

    this.downloadService.setData({
      menuContract: EnumDownloadMenuName.LABOR_CONTRACT,
      laborContractName: targetName,
      isLaborContractDownload: true
    });
    this.downloadService.setOpen(true);
    this.documentMohaService.getKeyDownloadContractByZip(targetId).subscribe(
      res => {
        if (res) {
          if (res.success === false || res.status === false || (res.code && res.code != '200' && res.code != 200)) {
            this.downloadService.setOpen(false);
            this.toastr.error(res.message || res.error || 'Tải file không thành công');
            return;
          }

          const downloadKey = typeof res === 'string' ? res : (res.key || res.data);
          if (!downloadKey || typeof downloadKey !== 'string') {
            this.downloadService.setOpen(false);
            this.toastr.error(res.message || 'Không thể lấy mã tải file');
            return;
          }

          const body = {
            key: downloadKey,
            token: sessionStorage.getItem('token'),
            lang: localStorage.getItem('__language')
          };

          this.documentMohaService.downloadFileZipBHS(body).subscribe(
            rs => {
              this.downloadService.setOpen(false);
              if (rs && rs.body) {
                if (rs.body.type === 'application/json') {
                  const reader = new FileReader();
                  reader.onload = () => {
                    try {
                      const errorRes = JSON.parse(reader.result as string);
                      this.toastr.error(errorRes.message || 'Lỗi tải file');
                    } catch (e) {
                      this.toastr.error('Lỗi tải file');
                    }
                  };
                  reader.readAsText(rs.body);
                  return;
                }

                const blob = rs.body;
                const disposition = rs.headers.get('content-disposition');
                const fileName = this.getFileName(disposition) || 'download.zip';

                if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                  window.navigator.msSaveOrOpenBlob(blob, fileName);
                  return;
                }

                const url = URL.createObjectURL(blob);
                const anchor = document.createElement('a');

                anchor.href = url;
                anchor.download = fileName;
                anchor.style.display = 'none';

                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();

                URL.revokeObjectURL(url);
              }
            },
            err => {
              this.downloadService.setOpen(false);
              this.toastr.error(err && err.message ? err.message : 'Error downloading file');
            }
          );
        } else {
          this.downloadService.setOpen(false);
          this.toastr.error('Tải file không thành công');
        }
      },
      err => {
        this.downloadService.setOpen(false);
        this.toastr.error(err && err.message ? err.message : 'Error fetching download key');
      }
    );
  }

  downloadContract(documentData: any) {
    if (documentData.id) {
      this.documentMohaService.downloadFileZip(documentData.id).subscribe(res => {
        const blobUrl = URL.createObjectURL(res);
        var a = document.createElement('a');
        a.href = blobUrl;
        
        a.download = (documentData.contractName || 'download') + '.zip'; 
        
        a.click();
        URL.revokeObjectURL(blobUrl);
      });
    }
  }
  private getFileName(disposition: string): string {
    if (!disposition) {
      return 'download.zip';
    }

    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
    if (utf8Match) {
      return decodeURIComponent(utf8Match[1]);
    }

    const normalMatch = /filename=\"?([^\";]+)\"?/i.exec(disposition);
    return normalMatch ? normalMatch[1] : 'download.zip';
  }

  assignAppendix(dossier: any) {
    if (!dossier) {
      return;
    }
    const data = {
      contractOriginalId: dossier.id,
      employeeEnterpriseId: dossier.idPersonal,
      worker: {
        // certificateNo: dossier.certificateNo,
        codeAndName: dossier.nameAndCode
      },
      ...dossier
    };
    const dialogRe = this.dialog.open(LinkDocumentAppendixDialogComponent, {
      width: '900px',
      data: data,
    });
    dialogRe.afterClosed().subscribe(rs => {
      if (rs) {
        this.loadData(this.paginate.currentPage, this.paginate.pageSize);
      }
    });
  }

  viewDetail(contract: any, appendix?: any) {
    let id = '';
    if (appendix) {
      id = appendix.id;
    } else {
      id = contract ? contract.id : '';
    }
    if (id) {
      this.toNavigate('/home-enterprise/view-document-moha/' + id, { goFrom: 'labor_profile' });
    }
  }

  deleteAppendix(dossier: DossierView, document: DossierDocumentView): void {
    let message = this.commonService.format(
      this.message.getMessage('messages.delete_file_appendix'),
      document.contractName || document.contractCode,
      dossier.nameAndCode
    );
    const dialogRef = this.dialog.open(WarningComponent, {
      width: '40%',
      data: message
    });
    dialogRef.afterClosed().subscribe(ref => {
      if (ref) {
        this.handleDelete(document.id);
      }
    });
  }

  handleDelete(id: string): void {
    this.documentMohaService.deleteAppendix(id).subscribe(res => {
      if (res.success) {
        this.toastr.success(this.message.getMessage('messages.delete_success'));
        this.loadData(this.paginate.currentPage, this.paginate.pageSize);
      } else {
        this.toastr.error(res.message);
      }
    });
  }

  toNavigate(route: string, params = {}) {
    let queryParams: any = {
      ...params,
      routeLink: '/home-enterprise/record-management-moha/',
    };
    const url = this.router.serializeUrl(this.router.createUrlTree([route], { queryParams }));
    window.open(url, '_blank');
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get selectedCount(): number {
    return this.selectedDossiers.size;
  }

  get resultTotal(): number {
    return this.paginate.totalRow;
  }

  get allVisibleSelected(): boolean {
    return this.filteredDossiers.length > 0 &&
      this.filteredDossiers.every(dossier => this.selectedDossiers.has(dossier.id));
  }

  applyFilter(): void {
    this.loadData(0, this.paginate.pageSize);
  }

  retryLoad(): void {
    this.loadData(this.paginate.currentPage, this.paginate.pageSize);
  }

  clearFilter(field: 'contractId' | 'employee' | 'nameOrCode'): void {
    this.filters[field] = '';
  }

  focusOutContractId(): void {
    this.filters.contractId = this.filters.contractId ? this.filters.contractId.trim() : '';
  }

  focusOutEmployee(): void {
    this.filters.employee = this.filters.employee ? this.filters.employee.trim() : '';
  }

  focusOutNameOrCode(): void {
    this.filters.nameOrCode = this.filters.nameOrCode ? this.filters.nameOrCode.trim() : '';
  }

  toggleExpanded(dossier: DossierView): void {
    if (!dossier.documents || dossier.documents.length === 0) {
      return;
    }
    if (this.expandedIds.has(dossier.id)) {
      this.expandedIds.delete(dossier.id);
    } else {
      this.expandedIds.add(dossier.id);
    }
    this.rebuildTableRows();
  }

  isExpanded(dossier: DossierView): boolean {
    return this.expandedIds.has(dossier.id);
  }

  toggleDossier(dossier: DossierView, checked: boolean): void {
    if (checked) {
      this.selectedDossiers.set(dossier.id, dossier);
    } else {
      this.selectedDossiers.delete(dossier.id);
    }
  }

  toggleAll(checked: boolean): void {
    this.filteredDossiers.forEach(dossier => {
      if (checked) {
        this.selectedDossiers.set(dossier.id, dossier);
      } else {
        this.selectedDossiers.delete(dossier.id);
      }
    });
  }

  isSelected(dossier: DossierView): boolean {
    return this.selectedDossiers.has(dossier.id);
  }

  emitAction(type: DossierListAction['type'], dossier?: DossierView, document?: DossierDocumentView): void {
    if (type === 'push-batch') {
      this.action.emit({ type, dossiers: Array.from(this.selectedDossiers.values()) });
      return;
    }
    this.action.emit({ type, dossier, document });
  }

  dossierStatusLabel(status: DossierCompletionStatus): string {
    return status === 'completed-all'
      ? (this.message.getMessage('record_management_moha.status.completed_all') || 'Hoàn thành cả bộ')
      : (this.message.getMessage('record_management_moha.status.incomplete_all') || 'Chưa hoàn thành cả bộ');
  }

  documentStatusLabel(status: DossierPlatformStatus): string {
    switch (status) {
      case 'completed': return this.message.getMessage('record_management_moha.status.completed') || 'Hoàn thành';
      case 'processing': return this.message.getMessage('record_management_moha.status.processing') || 'Đang xử lý';
      case 'failed': return this.message.getMessage('record_management_moha.status.failed') || 'Thất bại';
      default: return this.message.getMessage('record_management_moha.status.not_sent') || 'Chưa gửi';
    }
  }

  trackDossier(index: number, dossier: DossierView): string {
    return dossier.id;
  }

  trackDocument(index: number, document: DossierDocumentView): string {
    return document.id;
  }

  loadData(page: number = 0, pageSize: number = 10): void {
    const request: ContractCollectionFilterRequest = {
      mohaUuid: (this.filters.contractId || '').trim(),
      nameOrCodePersonal: (this.filters.employee || '').trim(),
      nameOrCodeContract: (this.filters.nameOrCode || '').trim(),
      statusMoha: this.toMohaStatus(this.filters.documentStatus),
      // API specification states this field must temporarily remain null.
      statusContractCollection: null,
      page,
      pageSize
    };

    this.loading = true;
    this.loadError = '';
    this.dossierService.filter(request).pipe(
      takeUntil(this.destroyed),
      finalize(() => this.loading = false)
    ).subscribe(response => {
      if (response && response.success === false) {
        this.handleLoadError(response.message);
        return;
      }

      const rows = this.extractRows(response);
      this.dossiers = rows.map(row => this.mapDossier(row));
      // statusContractCollection is temporarily unsupported by the API, so filter it locally.
      this.filteredDossiers = this.filters.dossierStatus ?
        this.dossiers.filter(dossier => dossier.status === this.filters.dossierStatus) :
        this.dossiers.slice();
      this.dossiers.forEach(dossier => {
        if (this.selectedDossiers.has(dossier.id)) {
          this.selectedDossiers.set(dossier.id, dossier);
        }
      });
      this.expandedIds.clear();
      this.filteredDossiers.slice(0, 0).forEach(dossier => this.expandedIds.add(dossier.id));
      this.updatePaginate(response, page, pageSize, rows.length);
      this.rebuildTableRows();
    }, error => {
      this.handleLoadError(error && error.message);
    });
  }

  private handleLoadError(message?: string): void {
    this.dossiers = [];
    this.filteredDossiers = [];
    this.tableRows = [];
    this.dataSource.setData([]);
    this.paginate = {
      currentPage: 0,
      pageSize: this.paginate.pageSize || 10,
      totalRow: 0,
      totalPage: 0,
      totalPageArray: [],
      no: []
    };
    this.loadError = message || 'Không thể tải danh sách bộ hợp đồng. Vui lòng thử lại.';
  }

  private updatePaginate(response: any, page: number, pageSize: number, rowCount: number): void {
    const payload = response && response.data != null ? response.data : response;
    const currentPage = payload && payload.currentPage != null ? Number(payload.currentPage) : page;
    const currentPageSize = payload && payload.pageSize != null ? Number(payload.pageSize) : pageSize;
    const totalRow = payload && payload.totalRow != null ? Number(payload.totalRow) : rowCount;
    const totalPage = currentPageSize > 0 ? Math.ceil(totalRow / currentPageSize) : 0;
    this.paginate = {
      currentPage,
      pageSize: currentPageSize,
      totalRow,
      totalPage,
      totalPageArray: this.counter(totalPage, 1),
      no: this.counter(currentPageSize, currentPage * currentPageSize + 1)
    };
  }

  private counter(length: number, from: number): number[] {
    return new Array(Math.max(0, length)).fill(0).map((item, index) => index + from);
  }

  private rebuildTableRows(): void {
    const rows: DossierTableRow[] = [];
    this.filteredDossiers.forEach((dossier, index) => {
      rows.push({
        type: 'dossier',
        dossier,
        number: this.paginate.currentPage * this.paginate.pageSize + index + 1
      });
      if (this.expandedIds.has(dossier.id)) {
        (dossier.documents || []).forEach(document => {
          rows.push({ type: 'document', dossier, document });
        });
      }
    });
    this.tableRows = rows;
    this.dataSource.setData(rows);
  }

  private extractRows(response: any): ContractCollectionResponse[] {
    const payload = response && response.data != null ? response.data : response;
    if (Array.isArray(payload)) {
      return payload;
    }
    if (payload && Array.isArray(payload.dataRow)) {
      return payload.dataRow;
    }
    if (payload && Array.isArray(payload.content)) {
      return payload.content;
    }
    if (payload && payload.id && Array.isArray(payload.contractAppendixItemDTOS)) {
      return [payload];
    }
    return [];
  }

  private mapDossier(row: ContractCollectionResponse): DossierView {
    const documents = (row.contractAppendixItemDTOS || [])
      .map((document, index) => this.mapDocument(document, index, row));
    return {
      id: row.id,
      mohaId: row.mohaUuid || '',
      nameAndCode: row.nameAndCode || '',
      customerCode: row.customerCode || (row as any).customerCode || '',
      employeeName: row.fullName || '',
      certificateNo: row.certificateNo || '',
      status: Number(row.mohaStatusFinal) === 1 ? 'completed-all' : 'incomplete-all',
      documents,
      employeeEnterpriseId: (row as any).idPersonal,
      raw: row
    };
  }

  private mapDocument(
    row: ContractCollectionDocumentResponse,
    index: number,
    parent: ContractCollectionResponse
  ): DossierDocumentView {
    return {
      id: row.id,
      mohaId: row.mohaUuid || '',
      contractCode: row.nameAndCode || '',
      contractName: row.nameAndCode || '',
      customerCode: row.customerCode || (row as any).personalCode ||
        parent.customerCode || (parent as any).personalCode || '',
      employeeName: row.fullName || parent.fullName || '',
      certificateNo: row.certificateNo || parent.certificateNo || '',
      status: this.mapMohaStatus(row.mohaStatus),
      appendix: index > 0,
      isCheckStandardized: row.isCheckStandardized
    };
  }

  private mapMohaStatus(status: number): DossierPlatformStatus {
    switch (Number(status)) {
      case 2: return 'processing';
      case 3: return 'failed';
      case 4: return 'completed';
      default: return 'not-sent';
    }
  }

  private toMohaStatus(status: DossierPlatformStatus | ''): number | null {
    switch (status) {
      case 'processing': return 2;
      case 'failed': return 3;
      case 'completed': return 4;
      case 'not-sent': return 1;
      default: return null;
    }
  }
}

export interface DossierPaginate {
  currentPage: number;
  pageSize: number;
  totalRow: number;
  totalPage: number;
  totalPageArray: number[];
  no: number[];
}

export class DossierTableDataSource extends DataSource<DossierTableRow> {
  private readonly data = new BehaviorSubject<DossierTableRow[]>([]);

  setData(rows: DossierTableRow[]): void {
    this.data.next(rows);
  }

  connect(): Observable<DossierTableRow[]> {
    return this.data.asObservable();
  }

  disconnect(): void {
    this.data.complete();
  }
}
